import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Skeleton,
  Pagination,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import AccountTransactionAdd from "./AccountTransactionAdd";
import AccountTransactionView from "./AccountTransactionView";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const typeCellSx = (value) => {
  if (value === "credit") {
    return { color: "success.main", fontWeight: 600 };
  }
  if (value === "debit") {
    return { color: "error.main", fontWeight: 600 };
  }
  return {};
};

const AccountTransactionIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["account-transactions", searchTerm, page, rowsPerPage],
    `shop/account-transactions/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/account-transactions", token);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const rows = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : rows.length;

  if (isError) {
    return <ErrorPage message={t("accountTransaction.failedToLoad")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">{t("accountTransaction.listTitle")}</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpenAdd(true)}>
          {t("accountTransaction.addNew")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("accountTransaction.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("accountTransaction.searchPlaceholder")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="account transaction table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.index")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.account")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.type")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("accountTransaction.transactionCurrency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("accountTransaction.exchangeRate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("accountTransaction.convertedAmount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("accountTransaction.accountCurrency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("accountTransaction.transactionDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("accountTransaction.description")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from(new Array(11)).map((__, j) => (
                      <TableCell key={`skeleton-${i}-${j}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row, i) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{row.account_name || "-"}</TableCell>
                    <TableCell sx={typeCellSx(row.transaction_type)}>
                      {row.transaction_type === "credit"
                        ? t("accountTransaction.credit")
                        : t("accountTransaction.debit")}
                    </TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.transaction_currency_title || "-"}</TableCell>
                    <TableCell>{row.exchange_rate}</TableCell>
                    <TableCell>{row.converted_amount}</TableCell>
                    <TableCell>{row.account_currency_title || "-"}</TableCell>
                    <TableCell>{row.transaction_date || "-"}</TableCell>
                    <TableCell>{row.description || "-"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setViewData(row);
                          setOpenView(true);
                        }}
                      >
                        {t("accountTransaction.view")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        {t("accountTransaction.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("accountTransaction.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      <ConfirmDialog />

      {openAdd && (
        <AccountTransactionAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openView && (
        <AccountTransactionView
          open={openView}
          onClose={() => setOpenView(false)}
          data={viewData}
        />
      )}
    </Box>
  );
};

export default AccountTransactionIndex;
